import { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { alertaCargandoProceso, alertaGeneral } from '../utils/alertas';
import InputFile from '../utils/InputFile';

const ConsentimientoDroxy = ({ 
  appointmentId, 
  onConsentimientoCompletado, 
  onConsentimientoCancelado,
  appointmentInfo 
}) => {
  const canvasRef = useRef(null);
  const [typeUserSignature, setTypeUserSignature] = useState("paciente");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [dataRepresentante, setDataRepresentante] = useState({
    fullName: "",
    idNumber: "",
    relationship: "",
  });

  const onHandleChangeDataRepresentante = (e) => {
    const { name, value } = e.target;
    setDataRepresentante((prevData) => ({
      ...prevData,
      [name]: value
    }));
  }

  const changeUserSignature = (e) => {
    const { value } = e.target;
    setTypeUserSignature(value);
    setDataRepresentante({
      fullName: "",
      idNumber: "",
      relationship: "",
    });
  }

  useEffect(() => {
    // Configurar el canvas al montar y redimensionar
    const setupCanvas = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext('2d');
        
        // Configurar tamaño del canvas
        canvas.width = rect.width || 400;
        canvas.height = 150;
        
        // Configurar estilo de dibujo
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
    };

    // Configurar inicialmente
    setupCanvas();

    // Reconfigurar en redimensionamiento
    const handleResize = () => {
      setTimeout(setupCanvas, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      
      // Restaurar scroll del body al desmontar (por si se desmonta mientras se dibuja)
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, []);

  const getCoordinates = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (event.touches && event.touches.length > 0) {
      return {
        x: (event.touches[0].clientX - rect.left) * scaleX,
        y: (event.touches[0].clientY - rect.top) * scaleY
      };
    } else {
      return {
        x: (event.clientX - rect.left) * scaleX,
        y: (event.clientY - rect.top) * scaleY
      };
    }
  };

  const startDrawing = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Prevenir scroll del body cuando se está dibujando
    if (event.touches) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    }
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    const { x, y } = getCoordinates(event);

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (event) => {
    if (!isDrawing) return;
    event.preventDefault();
    event.stopPropagation();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    const { x, y } = getCoordinates(event);

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Restaurar scroll del body
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFiles((prevFiles) => [...prevFiles, file]);
    }
  };

  const handleFileRemove = (fileIndex) => {
    setUploadedFiles((prevFiles) => prevFiles.filter((_, index) => index !== fileIndex));
  };

  const handleAccept = async () => {
    // Verificar si el canvas está vacío
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const canvasData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const isEmpty = canvasData.data.every((value) => value === 0);

    if (isEmpty) {
      alertaGeneral({
        titulo: 'Advertencia',
        messageHtml: 'Por favor, firme para continuar.',
        isWarning: true
      });
      return;
    }

    if (typeUserSignature === "representante" && (!dataRepresentante.fullName || !dataRepresentante.idNumber || !dataRepresentante.relationship)) {
      alertaGeneral({
        titulo: 'Advertencia',
        messageHtml: 'Por favor, complete todos los campos del representante legal.',
        isWarning: true
      });
      return;
    }

    alertaCargandoProceso({
      titulo: "Enviando consentimiento...",
      messageHtml: "Por favor, espere.",
      funcionAsync: async () => {
        try {
          // Crear un FormData para el endpoint de Droxy
          const formData = new FormData();

          // Agregar los datos básicos
          formData.append('appointmentId', appointmentId);
          formData.append('clinicId', appointmentInfo?.clinicId || '507f1f77bcf86cd799439011');
          formData.append('doctorId', appointmentInfo?.doctorId || '507f1f77bcf86cd799439012');
          formData.append('userSigning', typeUserSignature);

          // Construir patientInfo según el formato de Droxy
          const patientInfo = {
            patientId: appointmentInfo?.patientId || '507f1f77bcf86cd799439013',
            firstName: appointmentInfo?.firstName || 'Juan',
            secondName: appointmentInfo?.secondName || 'Carlos',
            firstLastName: appointmentInfo?.firstLastName || 'Pérez',
            secondLastName: appointmentInfo?.secondLastName || 'García',
            documentNumber: appointmentInfo?.documentNumber || '12345678',
            dob: appointmentInfo?.dob || '1990-01-01T00:00:00.000Z',
            idType: appointmentInfo?.idType || 'CC',
            cellNumber: appointmentInfo?.cellNumber || '573204125155',
            gender: appointmentInfo?.gender || 'male'
          };

          formData.append('patientInfo', JSON.stringify(patientInfo));

          // Agregar la firma como string base64
          const signatureDataUrl = canvas.toDataURL('image/png');
          formData.append('signature', signatureDataUrl);

          // Agregar datos del representante si aplica
          if (typeUserSignature === "representante") {
            formData.append('dataRepresentante', JSON.stringify(dataRepresentante));
          }

          // Agregar los archivos cargados
          uploadedFiles.forEach((file, index) => {
            formData.append(`files[${index}]`, file);
          });

          // Enviar la petición al endpoint de Droxy
          const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/droxy/consentimiento`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          if (response.data.success) {
            console.log("Consentimiento creado exitosamente:", response.data);
            alertaGeneral({
              titulo: '¡Consentimiento Aceptado con éxito!',
              messageHtml: 'El consentimiento ha sido aceptado correctamente.',
            }).then(result => {
              onConsentimientoCompletado();
            });
          } else {
            throw new Error(response.data.message || 'Error al crear el consentimiento');
          }
        } catch (error) {
          console.error('Error al enviar el consentimiento:', error);
          alertaGeneral({
            titulo: 'Error',
            messageHtml: error.response?.data?.message || 'Hubo un problema al enviar el consentimiento.',
            isError: true
          });
        }
      }
    });
  };

  const handleDecline = () => {
    alertaGeneral({
      titulo: 'Consentimiento Rechazado',
      messageHtml: 'Recuerde que si no acepta el consentimiento, no puede ingresar a la videoconsulta.',
      isWarning: true
    }).then(result => {
      if (result.isConfirmed) {
        onConsentimientoCancelado();
      }
    });
  };

  return (
    <div className="w-[90vw] md:w-[80vw] mx-auto border border-[#6e787aff] rounded-md flex flex-col items-center justify-center max-h-fit overflow-auto bg-gray-100 p-8 my-4 gap-2">
      <h2 className="text-2xl text-black text-center">SERVICIOS DE LA RED COLOMBIANA DE PRESTACIÓN Y CUIDADOS EN SALUD</h2>
      <h3 className="text-2xl text-black text-center">CONSENTIMIENTO INFORMADO</h3>

      <hr className="mt-4" />

      <div className="w-full max-w-md mb-3 space-y-4 ">
        <p className="text-lg text-black mb-2">Cargar documentos para revisión por doctor (opcional):</p>
        <InputFile fileName="Agregar un archivo" onChange={handleFileUpload} />
        <table className="w-full bg-white">
          <thead className="border-t border-b border-[#70706eff]">
            <tr>
              <th className="text-left border-r border-[#70706eff] px-4 py-2">Archivo</th>
              <th className="text-right border-l border-[#70706eff] px-4 py-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {uploadedFiles.map((file, index) => (
              <tr key={index} className="border-b border-gray-300">
                <td className="border-r border-gray-300 px-4 py-2">{file.name}</td>
                <td className="px-4 py-2">
                  <button onClick={() => handleFileRemove(index)} className="button-delete">
                    Eliminar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <hr className="my-4" />
      <div className="text-left mb-6 space-y-3 text-black">

        <div className="form-group">
          <p><strong>Descripción de la modalidad de consulta:</strong></p>
          <p>
            Consiste en la provisión de servicios de salud a distancia en los componentes de promoción, prevención, diagnóstico, tratamiento y rehabilitación, por profesionales de la salud que utilizan tecnologías de la información y la comunicación, que les permiten intercambiar datos con el propósito de facilitar el acceso y la oportunidad en la prestación de servicios a la población que presenta limitaciones de oferta, de acceso a los servicios o de ambos en su área geográfica. Este procedimiento permite la realización de interconsultas y el acceso de manera más ágil a la evaluación de la historia clínica por médicos especialistas en los términos de la Ley 1419 de 2010.
          </p>
        </div>

        <div className="form-group">
          <p><strong>Declaración del usuario:</strong></p>
          <p>
            Actuando a nombre propio comprendo que, en el curso de mi consulta médica, la información sobre mi persona, la cual en adelante se denominará "información personal", es útil para mi atención médica, autorizo que la misma pueda ser recolectada, almacenada y transmitida a personal médico de LA RED COLOMBIANA DE PRESTACIÓN Y CUIDADOS EN SALUD. Esta información puede incluir datos con los que se me puede identificar, como mi nombre, fecha de nacimiento e historia clínica. Asimismo, autorizo que en caso de que los médicos consideren pertinente me tomen fotografías, graben videos o audio y utilicen cualquier formato multimedia que sea necesario dentro de la modalidad de servicio de TELEMEDICINA, los cuales harán parte integrante de la historia clínica.
            Autorizo la utilización de cualquier equipo biomédico electrónico durante la prestación del servicio de salud en la modalidad de TELEMEDICINA.
            Autorizo que mi información personal pueda ser compartida y utilizada por LA RED COLOMBIANA DE PRESTACIÓN Y CUIDADOS EN SALUD y otros profesionales de la salud para manejar mi caso y asistir en la evaluación, el diagnóstico y el tratamiento de mi condición. Estos profesionales de la salud pueden habitar fuera de mi municipio de residencia y estar ubicados por fuera de la cobertura. Esta información puede ser transmitida en tiempo real utilizando equipos de videoconferencia donde el (los) médico(s) en el CENTRO DE REFERENCIA observarán, participarán y emitirán comentarios o sugerencias a distancia.
          </p>
        </div>

        <div className="form-group">
          <p><strong>Declaración de satisfacción con la información recibida:</strong></p>
          <p>
            Después de entender claramente la información suministrada, de tener la oportunidad de aclarar y resolver mis dudas e inquietudes sobre la modalidad de servicio de TELEMEDICINA, de contar con el espacio y tiempo suficientes, en el cual se me ha explicado satisfactoriamente la naturaleza y propósito de éste, manifiesto libre y voluntariamente mi consentimiento.
            Entiendo que de acuerdo al alcance de la ley vigente, LA RED COLOMBIANA DE PRESTACIÓN Y CUIDADOS EN SALUD no da garantías, seguros o promesas en relación con la seguridad de esta información.
          </p>
        </div>

        <div className="form-group">
          <p><strong>Extensión del presente consentimiento:</strong></p>
          <p>
            Yo consiento que la " LA RED COLOMBIANA DE PRESTACIÓN Y CUIDADOS EN SALUD " puede también utilizar mi información personal en un formulario aparte en el que no se identifica mi persona, para la realización de investigación médica, sin embargo, la " LA RED COLOMBIANA DE PRESTACIÓN Y CUIDADOS EN SALUD" no proveerá mi información personal a terceros sin mi autorización escrita previa, a excepción de la descrita en este consentimiento informado o en caso de ser requerido por la ley.
            Autorizo a la " LA RED COLOMBIANA DE PRESTACIÓN Y CUIDADOS EN SALUD "para que almacene, procese y transmita mi Información Personal a través de medios electrónicos, incluyendo Internet. Los servidores que utiliza la " LA RED COLOMBIANA DE PRESTACIÓN Y CUIDADOS EN SALUD " para almacenar, procesar y transmitir mi Información Personal pueden no localizarse en mi municipio de residencia. La " LA RED COLOMBIANA DE PRESTACIÓN Y CUIDADOS EN SALUD " utiliza medidas de seguridad para proteger la información almacenada, procesada y transmitida a través de medios electrónicos.
          </p>
        </div>

        <div className="form-group">
          <label> <strong>¿Quién firma?</strong> </label>
          <label className="w-fit flex items-center gap-2">
            <input
              type="radio"
              name="userSignature"
              value="paciente"
              checked={typeUserSignature === "paciente"}
              onChange={changeUserSignature}
              className="accent-blue-500"
            />
            Firma paciente
          </label>

          <label className="w-fit flex items-center gap-2">
            <input
              type="radio"
              name="userSignature"
              value="representante"
              checked={typeUserSignature === "representante"}
              onChange={changeUserSignature}
              className="accent-blue-500"
            />
            Firma representante legal
          </label>
        </div>

        {typeUserSignature === "paciente" ? (
          <div className="form-group">
            <p><strong>Aceptación de la prestación del servicio</strong></p>
            <p>
              De manera voluntaria y con pleno consentimiento autorizo que mi proceso de atención sea efectuado bajo la modalidad de servicio de Telemedicina, de igual manera estoy de acuerdo con las consultas médicas con especialistas e interconsultas por este medio.
              Al firmar debajo confirmo que he leído, comprendido por completo y estoy de acuerdo con todo lo anterior, o si no soy capaz de leer los párrafos previos, yo confirmo que este consentimiento me ha sido completamente leído y que estoy de acuerdo con todo su contenido.
            </p>
          </div>
        ) : (
          <div className="form-group">
            <p><strong>Consentimiento informado diferido:</strong></p>
            <p>
              Este numeral debe ser diligenciado por el representante legal del usuario cuando éste no tiene la capacidad legal para otorgar su consentimiento (menor de edad, inconsciencia, alteración mental temporal o definitiva).
              Dado que el beneficiario de los procedimientos es considerado incapaz o incompetente para tomar o rechazar la decisión o dar su consentimiento, el profesional me ha explicado, en mi calidad de representante legal, en forma clara, expresa y satisfactoria en qué consiste la telemedicina y sus implicaciones. He comprendido todo lo anterior y por ello de manera libre y voluntaria yo, &nbsp;

              <input type="text" className="px-1 border-b border-black focus:outline-none" placeholder="Nombre representante legal"
                name="fullName" value={dataRepresentante.fullName} onChange={onHandleChangeDataRepresentante}
              />

              &nbsp; identificado (a) con C.C. &nbsp;

              <input type="number" maxLength={10} className="px-1 border-b border-black focus:outline-none" placeholder="N.° cédula representante"
                name="idNumber" value={dataRepresentante.idNumber} onChange={onHandleChangeDataRepresentante}
              />

              &nbsp;, en mi calidad de &nbsp;

              <select name="relationship" className="border-b border-black focus:outline-none" onChange={onHandleChangeDataRepresentante}>
                <option value="">Parentezco</option>
                <option value="madre">Madre</option>
                <option value="padre">Padre</option>
                <option value="hermano">Hermano(a)</option>
                <option value="hijo">Hijo(a)</option>
                <option value="otros">Otros</option>
              </select>

              &nbsp; de <strong>{appointmentInfo?.firstName + " " + appointmentInfo?.firstLastName || "el paciente"}</strong>, doy mi consentimiento para que se le realice esta modalidad de consulta.
            </p>
          </div>
        )}

      </div>

      {/* Canvas para la firma digital */}
      <div className="mb-3">
        <p className="form-label text-black">Firma del {typeUserSignature}:</p>
        <div className="relative">
          <canvas
            ref={canvasRef}
            className="form-input w-full border-2 border-gray-300 rounded"
            style={{
              touchAction: 'none',
              cursor: 'crosshair',
              backgroundColor: '#f9f9f9'
            }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            onTouchCancel={stopDrawing}
          />
        </div>
      </div>

      {/* Botones de Aceptar y Rechazar */}
      <div className="flex space-x-4">
        <button
          onClick={handleAccept}
          className="button-send"
        >
          Aceptar
        </button>
        <button
          onClick={clearSignature}
          className="button-add"
        >
          Limpiar Firma
        </button>
        <button
          onClick={handleDecline}
          className="button-gray"
        >
          Rechazar
        </button>
      </div>
    </div>
  );
};

export default ConsentimientoDroxy; 